import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Palette,
  Type,
  Hash,
  Image,
  FileText,
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Upload,
  Loader2,
  ChevronRight,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  MessageSquare,
  Settings,
  Eye,
  Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import DashboardLayout from '../components/DashboardLayout';
import { api, isUpgradeError } from '../context/AuthContext';
import { toast } from 'sonner';

const BrandGuidelinesPage = () => {
  const [guidelines, setGuidelines] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('guidelines');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  
  const [selectedGuideline, setSelectedGuideline] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    colors: {
      primary: '#10B981',
      secondary: '#6366F1',
      accent: '#F59E0B',
      background: '#FFFFFF',
      text: '#1F2937'
    },
    typography: {
      heading_font: 'Inter',
      body_font: 'Open Sans',
      body_size: '16px'
    },
    voice_tone: {
      tone: 'professional',
      personality_traits: [],
      words_to_use: [],
      words_to_avoid: []
    },
    hashtag_rules: {
      branded_hashtags: [],
      max_hashtags: 10,
      min_hashtags: 0,
      required_hashtags: []
    },
    content_rules: {
      min_caption_length: 0,
      max_caption_length: 2200,
      require_call_to_action: false,
      forbidden_words: []
    }
  });
  
  // Validation state
  const [validateContent, setValidateContent] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [guidelinesRes, presetsRes] = await Promise.all([
        api.get('/brand-guidelines/'),
        api.get('/brand-guidelines/presets')
      ]);
      setGuidelines(guidelinesRes.data);
      setPresets(presetsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load brand guidelines');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setActionLoading(true);
    try {
      const res = await api.post('/brand-guidelines/', formData);
      setGuidelines([...guidelines, res.data]);
      setCreateDialogOpen(false);
      resetForm();
      toast.success('Brand guideline created');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to create brand guideline');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    setActionLoading(true);
    try {
      const res = await api.put(`/brand-guidelines/${selectedGuideline.guideline_id}`, formData);
      setGuidelines(guidelines.map(g => 
        g.guideline_id === selectedGuideline.guideline_id ? res.data : g
      ));
      setEditDialogOpen(false);
      resetForm();
      toast.success('Brand guideline updated');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to update brand guideline');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/brand-guidelines/${selectedGuideline.guideline_id}`);
      setGuidelines(guidelines.filter(g => g.guideline_id !== selectedGuideline.guideline_id));
      setDeleteDialogOpen(false);
      toast.success('Brand guideline deleted');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to delete brand guideline');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateFromPreset = async () => {
    if (!selectedPreset) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/brand-guidelines/from-preset/${selectedPreset.preset_id}`, null, {
        params: { name: `My ${selectedPreset.name}` }
      });
      setGuidelines([...guidelines, res.data]);
      setPresetDialogOpen(false);
      toast.success('Brand guideline created from preset');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to create from preset');
    } finally {
      setActionLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!validateContent.trim()) return;
    setValidating(true);
    try {
      const res = await api.post('/brand-guidelines/validate-content', null, {
        params: { content: validateContent }
      });
      setValidationResult(res.data);
    } catch (error) {
      toast.error('Failed to validate content');
    } finally {
      setValidating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      colors: {
        primary: '#10B981',
        secondary: '#6366F1',
        accent: '#F59E0B',
        background: '#FFFFFF',
        text: '#1F2937'
      },
      typography: {
        heading_font: 'Inter',
        body_font: 'Open Sans',
        body_size: '16px'
      },
      voice_tone: {
        tone: 'professional',
        personality_traits: [],
        words_to_use: [],
        words_to_avoid: []
      },
      hashtag_rules: {
        branded_hashtags: [],
        max_hashtags: 10,
        min_hashtags: 0,
        required_hashtags: []
      },
      content_rules: {
        min_caption_length: 0,
        max_caption_length: 2200,
        require_call_to_action: false,
        forbidden_words: []
      }
    });
    setSelectedGuideline(null);
  };

  const openEditDialog = (guideline) => {
    setSelectedGuideline(guideline);
    setFormData({
      name: guideline.name || '',
      description: guideline.description || '',
      colors: guideline.colors || formData.colors,
      typography: guideline.typography || formData.typography,
      voice_tone: guideline.voice_tone || formData.voice_tone,
      hashtag_rules: guideline.hashtag_rules || formData.hashtag_rules,
      content_rules: guideline.content_rules || formData.content_rules
    });
    setEditDialogOpen(true);
  };

  const toneOptions = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'authoritative', label: 'Authoritative' },
    { value: 'playful', label: 'Playful' }
  ];

  const fontOptions = [
    'Inter', 'Open Sans', 'Roboto', 'Lato', 'Poppins', 'Montserrat',
    'Playfair Display', 'Space Grotesk', 'Nunito', 'Source Sans Pro'
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="brand-guidelines-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Palette className="h-6 w-6 text-emerald-500" />
              Brand Guidelines
            </h1>
            <p className="text-slate-600 mt-1">Define and enforce brand consistency across your content</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setValidateDialogOpen(true)}
              data-testid="validate-content-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Validate Content
            </Button>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="create-guideline-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Guideline
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="guidelines" data-testid="tab-guidelines">My Guidelines</TabsTrigger>
            <TabsTrigger value="presets" data-testid="tab-presets">Presets</TabsTrigger>
          </TabsList>

          {/* My Guidelines Tab */}
          <TabsContent value="guidelines" className="space-y-6">
            {guidelines.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {guidelines.map((guideline, i) => (
                  <motion.div
                    key={guideline.guideline_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Card className="border-slate-100 hover:border-slate-200 transition-colors h-full">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">{guideline.name}</CardTitle>
                            <CardDescription className="line-clamp-2">
                              {guideline.description || 'No description'}
                            </CardDescription>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => openEditDialog(guideline)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-red-500 hover:text-red-600"
                              onClick={() => {
                                setSelectedGuideline(guideline);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Color Preview */}
                        {guideline.colors && (
                          <div>
                            <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                              <Palette className="h-3 w-3" /> Colors
                            </p>
                            <div className="flex gap-1">
                              {['primary', 'secondary', 'accent', 'background', 'text'].map(key => (
                                guideline.colors[key] && (
                                  <div
                                    key={key}
                                    className="w-8 h-8 rounded-md border border-slate-200"
                                    style={{ backgroundColor: guideline.colors[key] }}
                                    title={`${key}: ${guideline.colors[key]}`}
                                  />
                                )
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Typography */}
                        {guideline.typography && (
                          <div>
                            <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                              <Type className="h-3 w-3" /> Typography
                            </p>
                            <p className="text-sm text-slate-700">
                              {guideline.typography.heading_font} / {guideline.typography.body_font}
                            </p>
                          </div>
                        )}

                        {/* Voice & Tone */}
                        {guideline.voice_tone && (
                          <div>
                            <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" /> Voice
                            </p>
                            <Badge variant="secondary" className="capitalize">
                              {guideline.voice_tone.tone}
                            </Badge>
                          </div>
                        )}

                        {/* Content Rules Summary */}
                        <div className="flex flex-wrap gap-2">
                          {guideline.content_rules?.require_call_to_action && (
                            <Badge variant="outline" className="text-xs">CTA Required</Badge>
                          )}
                          {guideline.hashtag_rules?.min_hashtags > 0 && (
                            <Badge variant="outline" className="text-xs">
                              Min {guideline.hashtag_rules.min_hashtags} hashtags
                            </Badge>
                          )}
                          {guideline.logos?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {guideline.logos.length} logo(s)
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="border-slate-100">
                <CardContent className="text-center py-12">
                  <Palette className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Brand Guidelines Yet</h3>
                  <p className="text-slate-500 mb-4 max-w-md mx-auto">
                    Create brand guidelines to maintain consistency across all your social media content.
                  </p>
                  <div className="flex justify-center gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => setPresetDialogOpen(true)}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Start from Preset
                    </Button>
                    <Button 
                      onClick={() => setCreateDialogOpen(true)}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Custom
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Presets Tab */}
          <TabsContent value="presets" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              {presets.map((preset, i) => (
                <motion.div
                  key={preset.preset_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card className="border-slate-100 hover:border-emerald-200 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => {
                      setSelectedPreset(preset);
                      setPresetDialogOpen(true);
                    }}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-amber-500" />
                            {preset.name}
                          </CardTitle>
                          <CardDescription>{preset.description}</CardDescription>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Color Preview */}
                      {preset.colors && (
                        <div className="flex gap-1">
                          {['primary', 'secondary', 'accent', 'background', 'text'].map(key => (
                            <div
                              key={key}
                              className="w-10 h-10 rounded-lg border border-slate-200"
                              style={{ backgroundColor: preset.colors[key] }}
                            />
                          ))}
                        </div>
                      )}

                      {/* Voice Badge */}
                      {preset.voice_tone && (
                        <Badge variant="secondary" className="capitalize">
                          {preset.voice_tone.tone} tone
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setCreateDialogOpen(false);
            setEditDialogOpen(false);
            resetForm();
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editDialogOpen ? 'Edit Brand Guideline' : 'Create Brand Guideline'}
              </DialogTitle>
              <DialogDescription>
                Define your brand's visual identity and content rules
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="My Brand Guidelines"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Brief description of these guidelines..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Colors */}
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Palette className="h-4 w-4" /> Color Palette
                </Label>
                <div className="grid grid-cols-5 gap-3">
                  {['primary', 'secondary', 'accent', 'background', 'text'].map(colorKey => (
                    <div key={colorKey} className="space-y-1">
                      <Label className="text-xs capitalize">{colorKey}</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={formData.colors[colorKey]}
                          onChange={(e) => setFormData({
                            ...formData,
                            colors: {...formData.colors, [colorKey]: e.target.value}
                          })}
                          className="w-10 h-10 rounded cursor-pointer border-0"
                        />
                        <Input
                          value={formData.colors[colorKey]}
                          onChange={(e) => setFormData({
                            ...formData,
                            colors: {...formData.colors, [colorKey]: e.target.value}
                          })}
                          className="text-xs h-8 w-20"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Typography */}
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Type className="h-4 w-4" /> Typography
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Heading Font</Label>
                    <Select 
                      value={formData.typography.heading_font}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        typography: {...formData.typography, heading_font: value}
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {fontOptions.map(font => (
                          <SelectItem key={font} value={font}>{font}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Body Font</Label>
                    <Select 
                      value={formData.typography.body_font}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        typography: {...formData.typography, body_font: value}
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {fontOptions.map(font => (
                          <SelectItem key={font} value={font}>{font}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Voice & Tone */}
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <MessageSquare className="h-4 w-4" /> Voice & Tone
                </Label>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Tone</Label>
                    <Select 
                      value={formData.voice_tone.tone}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        voice_tone: {...formData.voice_tone, tone: value}
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {toneOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Words to Avoid (comma-separated)</Label>
                    <Input
                      value={formData.voice_tone.words_to_avoid.join(', ')}
                      onChange={(e) => setFormData({
                        ...formData,
                        voice_tone: {
                          ...formData.voice_tone,
                          words_to_avoid: e.target.value.split(',').map(w => w.trim()).filter(Boolean)
                        }
                      })}
                      placeholder="cheap, basic, maybe..."
                    />
                  </div>
                </div>
              </div>

              {/* Hashtag Rules */}
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Hash className="h-4 w-4" /> Hashtag Rules
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Min Hashtags</Label>
                    <Input
                      type="number"
                      min={0}
                      max={30}
                      value={formData.hashtag_rules.min_hashtags}
                      onChange={(e) => setFormData({
                        ...formData,
                        hashtag_rules: {...formData.hashtag_rules, min_hashtags: parseInt(e.target.value) || 0}
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max Hashtags</Label>
                    <Input
                      type="number"
                      min={0}
                      max={30}
                      value={formData.hashtag_rules.max_hashtags}
                      onChange={(e) => setFormData({
                        ...formData,
                        hashtag_rules: {...formData.hashtag_rules, max_hashtags: parseInt(e.target.value) || 30}
                      })}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs">Branded Hashtags (comma-separated)</Label>
                  <Input
                    value={formData.hashtag_rules.branded_hashtags.join(', ')}
                    onChange={(e) => setFormData({
                      ...formData,
                      hashtag_rules: {
                        ...formData.hashtag_rules,
                        branded_hashtags: e.target.value.split(',').map(w => w.trim()).filter(Boolean)
                      }
                    })}
                    placeholder="#mybrand, #brandname..."
                  />
                </div>
              </div>

              {/* Content Rules */}
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4" /> Content Rules
                </Label>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Min Caption Length</Label>
                      <Input
                        type="number"
                        min={0}
                        value={formData.content_rules.min_caption_length}
                        onChange={(e) => setFormData({
                          ...formData,
                          content_rules: {...formData.content_rules, min_caption_length: parseInt(e.target.value) || 0}
                        })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max Caption Length</Label>
                      <Input
                        type="number"
                        min={0}
                        value={formData.content_rules.max_caption_length}
                        onChange={(e) => setFormData({
                          ...formData,
                          content_rules: {...formData.content_rules, max_caption_length: parseInt(e.target.value) || 2200}
                        })}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Require Call-to-Action</Label>
                    <Switch
                      checked={formData.content_rules.require_call_to_action}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        content_rules: {...formData.content_rules, require_call_to_action: checked}
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Forbidden Words (comma-separated)</Label>
                    <Input
                      value={formData.content_rules.forbidden_words.join(', ')}
                      onChange={(e) => setFormData({
                        ...formData,
                        content_rules: {
                          ...formData.content_rules,
                          forbidden_words: e.target.value.split(',').map(w => w.trim()).filter(Boolean)
                        }
                      })}
                      placeholder="spam, scam, free..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateDialogOpen(false);
                setEditDialogOpen(false);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button 
                onClick={editDialogOpen ? handleUpdate : handleCreate}
                className="bg-emerald-500 hover:bg-emerald-600"
                disabled={actionLoading || !formData.name}
              >
                {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editDialogOpen ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Brand Guideline?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete "{selectedGuideline?.name}". This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Create from Preset Dialog */}
        <Dialog open={presetDialogOpen} onOpenChange={setPresetDialogOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create from Preset</DialogTitle>
              <DialogDescription>
                {selectedPreset 
                  ? 'Review and apply this preset' 
                  : 'Choose a preset template to get started'}
              </DialogDescription>
            </DialogHeader>
            {selectedPreset ? (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg">{selectedPreset.name}</h3>
                  <p className="text-slate-500 text-sm">{selectedPreset.description}</p>
                </div>
                
                {/* Preview Colors */}
                {selectedPreset.colors && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Color Palette</p>
                    <div className="flex gap-2">
                      {['primary', 'secondary', 'accent', 'background', 'text'].map(key => (
                        <div key={key} className="text-center">
                          <div
                            className="w-12 h-12 rounded-lg border border-slate-200 mb-1"
                            style={{ backgroundColor: selectedPreset.colors[key] }}
                          />
                          <p className="text-[10px] text-slate-400 capitalize">{key}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview Typography */}
                {selectedPreset.typography && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Typography</p>
                    <p className="text-sm">
                      <span className="font-medium">{selectedPreset.typography.heading_font}</span>
                      {' / '}
                      <span>{selectedPreset.typography.body_font}</span>
                    </p>
                  </div>
                )}

                {/* Preview Voice */}
                {selectedPreset.voice_tone && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Voice & Tone</p>
                    <Badge variant="secondary" className="capitalize">
                      {selectedPreset.voice_tone.tone}
                    </Badge>
                    {selectedPreset.voice_tone.personality_traits?.length > 0 && (
                      <p className="text-sm text-slate-600 mt-1">
                        {selectedPreset.voice_tone.personality_traits.join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {presets.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No presets available</p>
                ) : (
                  presets.map((preset) => (
                    <div
                      key={preset.preset_id}
                      className="p-3 border border-slate-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50/50 cursor-pointer transition-all"
                      onClick={() => setSelectedPreset(preset)}
                      data-testid={`preset-option-${preset.preset_id}`}
                    >
                      <h4 className="font-medium text-slate-900">{preset.name}</h4>
                      <p className="text-sm text-slate-500 line-clamp-1">{preset.description}</p>
                    </div>
                  ))
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setPresetDialogOpen(false); setSelectedPreset(null); }}>
                Cancel
              </Button>
              {selectedPreset ? (
                <Button 
                  onClick={handleCreateFromPreset}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  disabled={actionLoading}
                >
                  {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Use This Preset
                </Button>
              ) : null}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Content Validation Dialog */}
        <Dialog open={validateDialogOpen} onOpenChange={setValidateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Validate Content</DialogTitle>
              <DialogDescription>
                Check if your content follows your brand guidelines
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Post Content</Label>
                <Textarea
                  value={validateContent}
                  onChange={(e) => setValidateContent(e.target.value)}
                  placeholder="Enter your post content here..."
                  rows={5}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {validateContent.length} characters
                </p>
              </div>

              <Button 
                onClick={handleValidate}
                className="w-full"
                disabled={validating || !validateContent.trim()}
              >
                {validating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Validate
              </Button>

              {/* Validation Results */}
              {validationResult && (
                <div className="space-y-3">
                  <div className={`p-3 rounded-lg ${validationResult.valid ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2">
                      {validationResult.valid ? (
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                      )}
                      <span className={`font-medium ${validationResult.valid ? 'text-emerald-700' : 'text-red-700'}`}>
                        {validationResult.valid ? 'Content is valid!' : 'Issues found'}
                      </span>
                    </div>
                  </div>

                  {/* Warnings */}
                  {validationResult.warnings?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Warnings</p>
                      <div className="space-y-2">
                        {validationResult.warnings.map((warning, i) => (
                          <div 
                            key={i} 
                            className={`p-2 rounded text-sm ${warning.severity === 'error' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}
                          >
                            {warning.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Suggestions */}
                  {validationResult.suggestions?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Suggestions</p>
                      <div className="space-y-2">
                        {validationResult.suggestions.map((suggestion, i) => (
                          <div key={i} className="p-2 rounded bg-blue-50 text-blue-700 text-sm">
                            {suggestion.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BrandGuidelinesPage;
